import React, { useEffect, useState } from "react";
import Select from "react-select";
import { DatePicker } from "rsuite";

import PageTitle from "../../../layouts/PageTitle";
import {
  createTenant,
  getTenantUser,
  getTimeZone,
  mapUserWithTenant,
  updateTenant,
} from "../../../../services/TenantsService";
import { AsyncPaginate } from "react-select-async-paginate";
import { Button } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AddUserModal from "../../../components/superAdmin/AddUserModal";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import ImageCropModal from "../../../components/superAdmin/ImageCropModal";
import { getImageByFileName } from "../../../../services/academicService";

const AddTenant = () => {
  const [userData, setUserData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [value, setValue] = useState(null);
  const [userOptionsVersion, setUserOptionsVersion] = useState(0);
  const [timeZone, setTimeZone] = useState([]);
  const [showImageCropModal, setShowImageCropModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState();
  const [uploadedImageFiles, setUploadedImageFiles] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const editTenant = location.state?.tenant || null;
  const mode = location.state?.mode || "add";
  const isEditMode = mode === "edit" && !!editTenant;

  // ---- Form state ----
  const [formData, setFormData] = useState({
    name: "",
    shortName: "",
    phone: "",
    landLine: "",
    email: "",
    contactName: "",
    status: "",
    statusComment: "",
    latitude: "",
    longitude: "",
    timeZone: "",
    address: "",
    radius: "",
    isLocation: false,
  });

  const locationOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
  ];

  // Function to load options from API
  async function fetchUserData() {
    try {
      const res = await getTenantUser();
      setUserData(res.content);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }

  // Function to load options from API
  async function fetchTimeZone() {
    try {
      const res = await getTimeZone();
      setTimeZone(res);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }

  useEffect(() => {
    fetchTimeZone();
    fetchUserData();
  }, []);

  const loadOptions = async (search, loadedOptions, { page }) => {
    try {
      const response = await getTenantUser(
        page ?? 0, // 0-based
        10,
        search || "",
      );

      const options = response.content.map((user) => ({
        value: user.id,
        label: `${user.name} - ${user.email}`,
      }));

      const currentPage = response.number ?? page ?? 0;
      const totalPages = response.totalPages ?? 1;

      return {
        options,
        hasMore: currentPage + 1 < totalPages,
        additional: {
          page: currentPage + 1,
        },
      };
    } catch (error) {
      console.error("Error loading user options:", error);
      return {
        options: [],
        hasMore: false,
      };
    }
  };

  useEffect(() => {
    if (isEditMode && editTenant) {
      setFormData({
        name: editTenant.name || "",
        shortName: editTenant.shortName || "",
        phone: editTenant.phone || "",
        landLine: editTenant.landLine || "",
        email: editTenant.email || "",
        contactName: editTenant.contactName || "",
        status: editTenant.status || "",
        statusComment: editTenant.statusComment || "",
        timeZone: editTenant.timeZone || "",
        latitude: editTenant.latitude || "",
        longitude: editTenant.longitude || "",
        address: editTenant.address || "",
        radius: editTenant.radius || "",
        isLocation: editTenant.isLocation || false,
      });

      // Fetch and display existing logo preview
      if (editTenant.logo) {
        getImageByFileName(editTenant?.logo?.fileName)
          .then((res) => {
            const fileUrl =
              typeof res === "string" ? res : res?.fileUrl || res?.url || res;
            // Spread full logo object + add fileUrl for preview
            setUploadedImageFiles({ ...editTenant.logo, fileUrl });
          })
          .catch((err) => {
            console.error("Failed to load existing logo:", err);
            // Still store logo object even if preview fails
            setUploadedImageFiles(editTenant.logo);
          });
      }

    }
  }, [isEditMode, editTenant]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "shortName" ? value.replace(/\s/g, "") : value,
    }));
  };

  const handleStatusChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      status: selected?.value || "",
    }));
  };

  const handleUserCreated = (createdUser) => {
    const selectedUserOption = {
      value: createdUser?.id,
      label: `${createdUser?.name || createdUser?.studentName || ""} - ${createdUser?.email || ""}`,
    };

    setValue(selectedUserOption);
    setShowAddUserModal(false);
    setUserOptionsVersion((prev) => prev + 1);

    if (createdUser?.id) {
      setUserData((prev) => [
        createdUser,
        ...prev.filter((user) => user.id !== createdUser.id),
      ]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // const payload = {
    //   ...formData,
    //   ...(isEditMode && { id: editTenant.id }),
    // };

    const payload = {
      ...formData,
      latitude:
        formData.latitude !== "" && formData.latitude !== null
          ? Number(formData.latitude)
          : null,
      longitude:
        formData.longitude !== "" && formData.longitude !== null
          ? Number(formData.longitude)
          : null,
      radius: formData.radius !== "" ? parseInt(formData.radius) : 0,

      isLocation: Boolean(formData.isLocation),
      ownerId: value?.value || null,
      // Academy logo — always send the full metadata object
      // New upload has all fields; existing logo in edit mode also has them via editTenant.logo spread
      ...(uploadedImageFiles?.fileName && {
        logo: {
          fileName: uploadedImageFiles.fileName,
          bucketName: uploadedImageFiles.bucketName,
          fileUrl: uploadedImageFiles.fileUrl,
          contentType: uploadedImageFiles.contentType,
          size: uploadedImageFiles.size,
        },
      }),
      ...(isEditMode && { id: editTenant.id }),

    };

    try {
      if (isEditMode) {
        const res = await updateTenant(editTenant.id, payload);
        if (res) {
          toast.success("Tenant Updated successfully!", {
            position: "top-right",
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
          navigate("/super-admin/all-academies");
        }
      } else {
        const res = await createTenant(payload);

        if (res?.id && value?.value) {
          // const mapPayload = {
          //   userId: value.value,
          //   tenantId: res?.id,
          // };
          // await mapUserWithTenant(mapPayload);
          toast.success("Academy created successfully!", {
            position: "top-right",
            autoClose: 1000,
          });
          navigate("/super-admin/all-academies");
        }
      }
    } catch (error) {
      const message =
        error?.response?.data?.errorMessage ||
        error?.message ||
        "Something went wrong";
      Swal.fire("Error!", message, "error");
    } finally {
      setLoading(false);
    }
  };

  const timeZoneOptions = timeZone.map((tz) => ({
    value: tz.timeZoneId,
    label: `(${tz.offset}) ${tz.timeZoneId} - ${tz.displayName} `,
  }));

  return (
    <>
      <PageTitle
        activeMenu={isEditMode ? "Edit Academy" : "Add Academy"}
        motherMenu={"Academies"}
      />

      <div className="row">
        <div className="col-xl-12 col-xxl-12 col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">
                {isEditMode ? "Edit Academy" : "Add Academy"}
              </h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Tenant Name */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="tenantName">
                        Academy Name<span className="text-danger">*</span>
                      </label>
                      <input
                        id="tenantName"
                        name="name"
                        placeholder="Enter Academy Name"
                        type="text"
                        className="form-control"
                        required
                        value={formData.name}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="shortName">
                        Academy Short Name<span className="text-danger">*</span>
                      </label>
                      <input
                        id="shortName"
                        name="shortName"
                        placeholder="Enter Academy Short Name"
                        type="text"
                        className="form-control"
                        required
                        value={formData.shortName}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="email">
                        Email<span className="text-danger">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        placeholder="Enter Email"
                        type="email"
                        className="form-control"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        readOnly={isEditMode}
                      />
                    </div>
                  </div>

                  {/* Mobile Number -> phone */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="mobileNumber">
                        Mobile Number<span className="text-danger">*</span>
                      </label>
                      <input
                        id="mobileNumber"
                        name="phone"
                        placeholder="Enter Mobile Number"
                        type="number"
                        className="form-control"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Land Line */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="landLine">
                        Land Line
                      </label>
                      <input
                        id="landLine"
                        name="landLine"
                        placeholder="Enter Landline Number"
                        type="number"
                        className="form-control"
                        value={formData.landLine}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Contact Name */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="contactName">
                        Contact Name<span className="text-danger">*</span>
                      </label>
                      <input
                        id="contactName"
                        name="contactName"
                        placeholder="Enter Contact Name"
                        type="text"
                        className="form-control"
                        required
                        value={formData.contactName}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Status */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">
                        Status<span className="text-danger">*</span>
                      </label>
                      <Select
                        isSearchable={false}
                        required
                        options={[
                          { value: "ACTIVE", label: "Active" },
                          { value: "INACTIVE", label: "Inactive" },
                        ]}
                        placeholder="Select Status"
                        className="custom-react-select"
                        value={
                          formData.status
                            ? {
                              value: formData.status,
                              label:
                                formData.status === "ACTIVE"
                                  ? "Active"
                                  : "Inactive",
                            }
                            : null
                        }
                        onChange={handleStatusChange}
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="timeZone">
                        Time Zone<span className="text-danger">*</span>
                      </label>
                      <Select
                        isSearchable={true}
                        required
                        options={timeZoneOptions}
                        placeholder="Select Time Zone"
                        className="custom-react-select"
                        value={
                          timeZoneOptions.find(
                            (option) => option.value === formData.timeZone,
                          ) || null
                        }
                        onChange={(selected) =>
                          setFormData((prev) => ({
                            ...prev,
                            timeZone: selected ? selected.value : "",
                          }))
                        }
                      />
                    </div>
                  </div>

                  {/* Users + Add icon (same row) */}
                  {/* Users + Add icon (same row) */}
                  {!isEditMode && (
                    <>
                      <div className="col-sm-6">
                        <div className="form-group">
                          <label className="form-label">
                            Users<span className="text-danger">*</span>
                          </label>

                          <AsyncPaginate
                            value={value}
                            loadOptions={loadOptions}
                            onChange={setValue}
                            additional={{ page: 0 }}
                            cacheUniqs={[userOptionsVersion]}
                            isSearchable={true}
                            placeholder="Select User"
                            className="custom-react-select"
                            required
                          />
                        </div>
                      </div>

                      <div className="col-sm-4 d-flex align-items-end">
                        <div className="form-group">
                          <Button
                            className="me-2 p-2"
                            variant="primary "
                            title="Add"
                            type="button"
                            // optional: you can trigger something here later
                            onClick={() => setShowAddUserModal(true)}
                          >
                            Add User
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="latitude">
                        Latitude<span className="text-danger">*</span>
                      </label>
                      <input
                        id="latitude"
                        name="latitude"
                        placeholder="Enter Latitude"
                        type="text"
                        className="form-control"
                        required
                        value={formData.latitude}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="longitude">
                        Longitude<span className="text-danger">*</span>
                      </label>
                      <input
                        id="longitude"
                        name="longitude"
                        placeholder="Enter Longitude"
                        type="text"
                        className="form-control"
                        required
                        value={formData.longitude}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Radius (meters)</label>

                      <input
                        type="number"
                        name="radius"
                        placeholder="Enter Radius"
                        className="form-control"
                        value={formData.radius}
                        onChange={handleChange}
                        min="0"
                      />
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Location Check</label>

                      <Select
                        options={locationOptions}
                        placeholder="Select Location Check"
                        className="custom-react-select"
                        value={locationOptions.find(
                          (option) => option.value === formData.isLocation,
                        )}
                        onChange={(selected) =>
                          setFormData((prev) => ({
                            ...prev,
                            isLocation: selected.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  {/* Status Comment */}
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <div className="form-group">
                      <label className="form-label" htmlFor="statusComment">
                        Status Comment
                      </label>
                      <textarea
                        id="statusComment"
                        name="statusComment"
                        placeholder="Enter Comment"
                        className="form-control"
                        rows="3"
                        value={formData.statusComment}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* //address fields can be added here in future */}
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <div className="form-group">
                      <label className="form-label" htmlFor="address">
                        Address{" "}
                      </label>
                      <textarea
                        id="address"
                        name="address"
                        placeholder="Enter Address"
                        className="form-control"
                        rows="3"
                        value={formData.address}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* =================================================
    ACADEMY LOGO
================================================= */}

                  <div className="col-sm-12">
                    <div className="form-group mb-3">
                      <label className="form-label fw-semibold">
                        Academy Logo
                      </label>

                      <div
                        className="d-flex align-items-center"
                        style={{
                          gap: "12px",
                          minHeight: "70px",
                        }}
                      >
                        {/* Logo preview - only shown after upload */}
                        {uploadedImageFiles?.fileUrl && (
                          <div
                            style={{
                              width: "68px",
                              height: "68px",
                              border: "1px solid #e2e6ea",
                              borderRadius: "8px",
                              backgroundColor: "#f8f9fa",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={uploadedImageFiles.fileUrl}
                              alt="Academy Logo"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                padding: "5px",
                              }}
                            />
                          </div>
                        )}

                        {/* Upload area */}
                        <div>
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={() => setShowImageCropModal(true)}
                            style={{
                              borderRadius: "6px",
                              padding: "7px 12px",
                              fontSize: "13px",
                              fontWeight: 500,
                            }}
                          >
                            <i
                              className="fa fa-upload"
                              style={{
                                marginRight: "6px",
                              }}
                            />

                            {uploadedImageFiles?.fileUrl
                              ? "Change Logo"
                              : "Upload Logo"}
                          </Button>

                          <div
                            className="text-muted"
                            style={{
                              fontSize: "11px",
                              marginTop: "4px",
                            }}
                          >
                            PNG, JPG or WEBP • Max 10 MB
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <button
                      type="submit"
                      className="btn btn-primary me-1"
                      disabled={loading}
                    >
                      {loading
                        ? isEditMode
                          ? "Updating..."
                          : "Submitting..."
                        : isEditMode
                          ? "Update"
                          : "Submit"}
                    </button>

                    <Link
                      to="/super-admin/all-academies"
                      className="btn btn-danger light"
                    >
                      Cancel
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <AddUserModal
        show={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        onUserCreated={handleUserCreated}
      />
      <ImageCropModal
        show={showImageCropModal}
        onClose={() => setShowImageCropModal(false)}
        existingImage=""
        onUploadSuccess={(response) => {
          // Only after successful upload
          // show image on page
          setUploadedImageFiles(response);

          // Close crop modal
          setShowImageCropModal(false);
        }}
      />
    </>
  );
};

export default AddTenant;
